import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CardContent,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import styles from "./TermsAndConditions.module.scss";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";

interface TermsAndConditionsProps {
  content: string;
  handleOptInProceed: () => void;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  content,
  handleOptInProceed,
}) => {
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [isChecked, setIsChecked] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const sanitizedContent = DOMPurify.sanitize(content, {
    ADD_ATTR: ["target", "rel"],
  });
  const { t } = useTranslation();
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          setIsScrolledToEnd(true);
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      const { scrollHeight, clientHeight } = contentElement;
      if (scrollHeight <= clientHeight) {
        setIsScrolledToEnd(true);
      } else {
        contentElement.addEventListener("scroll", handleScroll);
      }
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  return (
    <>
      <CardContent
        sx={{
          padding: 0,
          ":last-child": {
            paddingBottom: 0,
          },
        }}
      >
        <Box
          ref={contentRef}
          className={styles.tacContainer}
          sx={{
            maxHeight: "350px",
            overflowY: "auto",
            margin: "1rem 0",
            // Custom scrollbar styles
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#4e7e6e",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      </CardContent>

      <Box
        sx={{
          display: { xs: "flex", md: "flex" },
          flexDirection: { xs: "column", md: "row" }, // stack on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" },
          alignItems: "center",
          gap: 2, // optional spacing between elements
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
              disabled={!isScrolledToEnd}
            />
          }
          label={t("termConditions")}
        />

        <Box
          sx={{
            width: { xs: "100%", md: "auto" }, // full width on mobile so it drops to next row
            display: "flex",
            justifyContent: { xs: "flex-start", md: "initial" }, // align button to right on mobile
            marginTop: { xs: 2, md: 0 }, // add spacing between rows on mobile
          }}
        >
          <CustomButton
            variant="contained"
            onClick={handleOptInProceed}
            disabled={!isChecked}
            text={t("continue")}
            fullWidth={isMobile ? true : false}
          />
        </Box>
      </Box>
    </>
  );
};

export default TermsAndConditions;
